import React, { useState, useEffect } from "react";

import SpectrumChart from "../Charts/SpectrumChart";
import { CreateSpectrumPoints } from "../../utils/CreatePoints";
import SimpleDataGrid from "../Tables/SimpleDataGrid";
import BaseAlertModal from "./BaseAlertModal";
import {
    DATA_TYPES,
    FIELD_TYPES,
    NULL_OPTION,
    MODEL_PROCESSING_TYPES,
} from "../../utils/constants";
import Select from "react-select";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import ProductTags from "../Tags/ProductTags";
import { longDateTime } from "../../utils/DateHelper";

import { Trans, useTranslation } from "react-i18next";
import { cloneDeep, isEmpty, isNil } from "lodash";
import MainLoadingButton from "../Buttons/MainLoadingButton";

const ScanDetailsModalBody = ({
    data,
    setData,
    fields,
    afterDeleteReadings,
    showSpectralData,
    saveModal,
    setSaveModal,
    inputChange,
    editMode,
    scanId,
    updateScan,
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [deletedPoints, setDeletedPoints] = useState([]);
    const [spectrumPoints, setSpectrumPoints] = useState([]);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveAndSyncLoading, setSaveAndSyncLoading] = useState(false);
    const { t } = useTranslation();

    const headers = [
        t("parameterName"),
        t("predictedValue"),
        t("referenceValue"),
        t("version"),
        t("additions"),
    ];
    const tabs = [t("data"), t("spectrum")];

    useEffect(() => {
        handleResetingSelectedRows();
        handleSpectrumPointsCreation();
    }, [data]);

    const handleResetingSelectedRows = () => {
        if (!isEmpty(data)) {
            setSelectedRows([]);
        }
    };

    const handleSpectrumPointsCreation = () => {
        if (data && data.spectralData && showSpectralData) {
            setSpectrumPoints(
                CreateSpectrumPoints(
                    {
                        rows: Object.values(data.spectralData),
                        headers: ["wvn", ...data.headers],
                        ids: Object.keys(data.spectralData).map((_, i) => i),
                    },
                    true
                )
            );
        }
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                switch (field.type) {
                    case FIELD_TYPES.Text:
                        return (
                            <div key={index} className="input-group m-0">
                                <label
                                    htmlFor={`input-${index}`}
                                    className="uppercase"
                                >
                                    {field.label}
                                </label>
                                <input
                                    type={field.type}
                                    id={`input-${index}-${field.name}`}
                                    className={`input-element ${
                                        !field.disabled && "disabled"
                                    }`}
                                    name={field.name}
                                    value={getFieldValue(field) || "--"}
                                    onChange={inputChange}
                                    disabled={!field.disabled}
                                />
                            </div>
                        );
                    case FIELD_TYPES.Dropdown:
                        return (
                            <div key={index} className="input-group m-0">
                                <label
                                    htmlFor={`input-${index}`}
                                    className="uppercase"
                                >
                                    {field.label}
                                </label>

                                <Select
                                    options={field.options}
                                    isDisabled={!field.disabled}
                                    value={data[field.name] || NULL_OPTION}
                                    onChange={(value, action) => {
                                        handleSelectChange(
                                            field.name,
                                            value,
                                            action
                                        );
                                    }}
                                    placeholder={t("selectPlaceholder", {
                                        entity: field.placeholder,
                                    })}
                                    styles={getReactSelectStyling({
                                        valueContainerStyle: false,
                                        disabledCustomStyling: false,
                                    })}
                                    theme={getReactSelectTheme}
                                    isClearable={field.isClearable}
                                />
                            </div>
                        );
                    default:
                        return (
                            <div key={index} className="input-group m-0">
                                <label
                                    htmlFor={`input-${index}`}
                                    className="uppercase"
                                >
                                    {field.label}
                                </label>
                                <input
                                    type="text"
                                    id={`input-${index}-${field.name}`}
                                    className={`input-element ${
                                        !field.disabled && "disabled"
                                    }`}
                                    name={field.name}
                                    value={getFieldValue(field) || "--"}
                                    onChange={inputChange}
                                    disabled={!field.disabled}
                                />
                            </div>
                        );
                }
            })
        );
    };

    const renderAdditions = () => {
        return (
            data.additions &&
            Object.keys(data.additions).map((additionKey) => {
                let value = data.additions[additionKey];
                let valueObject;
                let isValid = true;

                if (value?.type == DATA_TYPES.object) {
                    valueObject = renderTags(value.value, "justify-start");
                    isValid = !!valueObject;
                } else {
                    if (isNil(value?.value)) {
                        value =
                            typeof value == DATA_TYPES.object ? "--" : value;
                    } else {
                        value = value?.value;
                    }
                }

                if (!isValid) return;
                return (
                    <div key={additionKey} className="input-group m-0">
                        <label
                            htmlFor={`input-${additionKey}`}
                            className="uppercase"
                        >
                            {additionKey}
                        </label>
                        {valueObject || (
                            <input
                                type="text"
                                id={`input-${additionKey}`}
                                className="input-element disabled"
                                name={additionKey}
                                value={value ?? "--"}
                                readOnly
                            />
                        )}
                    </div>
                );
            })
        );
    };

    const renderHeader = () => {
        return (
            headers &&
            headers.map((header, index) => {
                return (
                    <td key={index}>
                        <div className="font-bold px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center justify-center">
                                <span className="ml-2">{header}</span>
                            </div>
                        </div>
                    </td>
                );
            })
        );
    };

    const renderPredictedValues = () => {
        return (
            data.predictions &&
            Object.keys(data.predictions).map((key, index) => {
                return (
                    <tr key={index}>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {key}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {getPredictedValue(data.predictions[key])}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {editMode ? (
                                    <input
                                        type="number"
                                        className="input-element table-element"
                                        name={"referenceValue"}
                                        value={
                                            data.predictions[key]
                                                .referenceValue ?? 0
                                        }
                                        onChange={(e) => inputChange(e, key)}
                                    />
                                ) : (
                                    getReferenceValue(data.predictions[key])
                                )}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {getVersionName(data.predictions[key])}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {getAdditions(data.predictions[key])}
                            </div>
                        </td>
                    </tr>
                );
            })
        );
    };

    const getFieldValue = (field) => {
        if (field.name === "collectedBy") {
            return data["collectedBy"]?.label;
        } else {
            if (field.type === FIELD_TYPES.DateTime) {
                return longDateTime(data[field.name]);
            } else {
                return data[field.name] || "--";
            }
        }
    };

    const renderTabs = () => {
        return (
            tabs &&
            tabs.map((tab, index) => {
                return (
                    <div
                        key={index}
                        onClick={() => {
                            setActiveTab(index);
                        }}
                        className={`tabs-bnt-item ${
                            activeTab === index ? "active" : ""
                        }`}
                        data-tabs="data-content-wrapper"
                    >
                        {tab}
                    </div>
                );
            })
        );
    };

    const renderHistoryFields = () => {
        return (
            <>
                <div className="input-group m-0">
                    <label className="uppercase">{t("lastModifiedAt")}</label>
                    <ProductTags
                        tags={[longDateTime(data.lastUpdatedAt)]}
                        listClassName="text-12"
                    />
                </div>
                <div className="input-group m-0">
                    <label className="uppercase">{t("lastModifiedBy")}</label>
                    <ProductTags tags={[data.lastUpdatedBy]} />
                </div>
            </>
        );
    };

    const renderTags = (tags, className = "justify-center") => {
        const limit = 10;
        const itemsArr = Object.keys(tags || {});
        const items = itemsArr.slice(0, limit)?.filter((k) => !isNil(tags[k]));

        if (!itemsArr.length || !items.length) return;
        return (
            <ul
                className={`tags-wrapper flex items-center flex-wrap ${className}`}
            >
                {items?.map((key, index) => (
                    <li
                        key={index}
                        className="tags-items flex gap-1 whitespace-nowrap"
                    >
                        <span className="font-semibold">{key}:</span>
                        <span>{tags[key]}</span>
                    </li>
                ))}

                {itemsArr.length > limit && (
                    <li
                        key="empty-tag"
                        className="tags-items flex gap-1 whitespace-nowrap"
                    >
                        <span>...</span>
                    </li>
                )}
            </ul>
        );
    };

    const handleSelectChange = (fieldName, selectionObj, action) => {
        if (action.action === "clear") {
            const clonedObj = cloneDeep(data);
            delete clonedObj[fieldName];
            setData(clonedObj);
        } else setData((prev) => ({ ...prev, [fieldName]: selectionObj }));
    };

    const getPredictedValue = (param) => {
        const value = param?.value ? param.value : t("invalid");
        return param?.type === MODEL_PROCESSING_TYPES.Classification?.value
            ? `${param.label} (${value})`
            : value;
    };

    const getReferenceValue = (param) => {
        return param?.referenceValue ?? "--";
    };

    const getVersionName = (param) => {
        return param?.versionId ? param.versionName : "--";
    };
    const getAdditions = (param) => {
        return renderTags(param?.additions) || "--";
    };

    const renderSyncAndSaveButton = () => {
        return (
            <MainLoadingButton
                isDisabled={saveAndSyncLoading || saveLoading}
                onClick={() =>
                    updateScan({
                        sync: true,
                        setLoader: setSaveAndSyncLoading,
                    })
                }
                buttonStyle={`default-orange-btn ml-2 ${
                    saveAndSyncLoading || saveLoading ? "disabled" : ""
                }`}
                isRequesting={saveAndSyncLoading}
                buttonText={t("syncAndSave")}
            />
        );
    };

    return (
        <div className="p-5 pt-0">
            <div className="input-group mb-0">
                <label className="uppercase mb-0">{t("settings")}</label>
            </div>
            <hr className="mb-3 mt-1" />
            <div className="main-customer-form-inputs grid grid-cols-2 gap-3 px-2">
                {renderFields()}
            </div>
            <div className="input-group mt-3 px-2">
                <label htmlFor="note" className="uppercase">
                    <Trans i18nKey="note">Note</Trans>
                </label>
                <textarea
                    value={data.note || "---"}
                    id="note"
                    className={`input-element ${
                        !editMode && "textarea-disabled"
                    } textarea-element`}
                    onChange={inputChange}
                    readOnly={!editMode}
                    name={"note"}
                />
            </div>
            {!isEmpty(data.additions) && (
                <>
                    <div className="input-group mb-0">
                        <label className="uppercase mb-0">
                            {t("advancedSettings")}
                        </label>
                    </div>
                    <hr className="mb-3 mt-1" />
                    <div className="main-customer-form-inputs grid grid-cols-2 gap-2 mt-1 px-2">
                        {renderAdditions()}
                    </div>
                </>
            )}
            {data.predictions && Object.keys(data.predictions).length > 0 && (
                <div className="prediction-table mt-4">
                    <div className="input-group m-0">
                        <label
                            htmlFor="scan-input-1"
                            className="uppercase mb-0"
                        >
                            <Trans i18nKey="prediction">Prediction</Trans>
                        </label>
                    </div>
                    <hr className="mb-3 mt-1" />
                    <div className="table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y mx-2">
                        <table
                            className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center w-full"
                            style={{ maxHeight: "350px" }}
                        >
                            <thead>
                                <tr>{renderHeader()}</tr>
                            </thead>
                            <tbody>{renderPredictedValues()}</tbody>
                        </table>
                    </div>
                </div>
            )}
            {showSpectralData && (
                <div className="mt-2 mb-5 px-2">
                    <div className="tabs-wrapper">
                        <div className="tabs-btn">{renderTabs()}</div>
                        <div className="tabs-content">
                            <SimpleDataGrid
                                visible={activeTab === 0}
                                data={data}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                scanId={scanId}
                                afterDeleteReadings={afterDeleteReadings}
                            />

                            <SpectrumChart
                                visible={activeTab === 1}
                                points={spectrumPoints}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                deletedPoints={deletedPoints}
                                setDeletedPoints={setDeletedPoints}
                                renderSwitch={false}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className="input-group mb-0 mt-5">
                <label className="uppercase mb-0">{t("history")}</label>
            </div>
            <hr className="mb-3 mt-1" />
            <div className="main-customer-form-inputs grid grid-cols-2 gap-3 px-2">
                {renderHistoryFields()}
            </div>

            <BaseAlertModal
                isVisible={saveModal}
                setIsVisible={setSaveModal}
                iconClassName="fas fa-exclamation-triangle"
                action={{
                    submitAction: () =>
                        updateScan({ setLoader: setSaveLoading }),
                    name: t("save"),
                    disabled: saveLoading || saveAndSyncLoading,
                }}
                isSubmitting={saveLoading}
                secondActionButton={renderSyncAndSaveButton()}
                message={t("scanSaveWarningMessage")}
            ></BaseAlertModal>
        </div>
    );
};

export default ScanDetailsModalBody;
